import { FC, ReactNode } from 'react';
import styled from 'styled-components';
import { ViewModeSwitcher } from './ViewModeSwitcher';

interface NgSectionProps {
  label: string;
  moreLabel?: string;
  headerSlot?: ReactNode;
  headerAction?: ReactNode;
  children?: ReactNode;
  moreCount?: number;
  onShowMore?: () => void;
  isExpanded?: boolean;
  viewMode?: 'grid' | 'list';
  onViewModeChange?: (mode: 'grid' | 'list') => void;
}

const Section = styled.section`
  background: ${({ theme }) => theme?.palette?.white ?? '#ffffff'};
  border-radius: ${({ theme }) => theme?.radius?.card ?? '4px'};
  box-shadow: ${({ theme }) => theme?.shadows?.card ?? '0 2px 8px rgba(0, 0, 0, 0.08)'};
  padding: ${({ theme }) => theme?.spacing?.default ?? '1rem'} ${({ theme }) => theme?.spacing?.medium ?? '1.5rem'};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
`;

const Label = styled.span`
  text-transform: uppercase;
  font-weight: 700;
  letter-spacing: 0.07em;
  font-size: ${({ theme }) => theme?.fontSize?.xs ?? '11px'};
  color: ${({ theme }) => theme?.palette?.gray ?? '#64748b'};
  font-family: ${({ theme }) => theme?.fontFamily?.main ?? 'sans-serif'};
`;

const ShowMoreRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
`;

export const NgSectionCount = styled.span`
  font-weight: 500;
  font-size: ${({ theme }) => theme?.fontSize?.xs ?? '11px'};
  color: ${({ theme }) => theme?.palette?.textDimmed ?? '#64748b'};
  font-family: ${({ theme }) => theme?.fontFamily?.main ?? 'sans-serif'};
`;

export const NgSectionLink = styled.a`
  cursor: pointer;
  font-weight: 600;
  white-space: nowrap;
  text-decoration: none;
  font-size: ${({ theme }) => theme?.fontSize?.sm ?? '0.75rem'};
  color: ${({ theme }) => theme?.palette?.textLinkColor ?? '#0A6EFF'};
  font-family: ${({ theme }) => theme?.fontFamily?.main ?? 'sans-serif'};

  &:hover {
    text-decoration: underline;
    color: ${({ theme }) => theme?.palette?.textLinkColor ?? '#215eff'};
  }
`;

export const NoDataSectionMessage = styled.div`
  min-height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme?.palette?.textDimmed ?? '#94a3b8'};
  font-size: ${({ theme }) => theme?.fontSize?.sm ?? '0.875rem'};
  font-family: ${({ theme }) => theme?.fontFamily?.main ?? 'sans-serif'};
`;

export const NgSection: FC<NgSectionProps> = ({
  label,
  moreLabel,
  headerSlot,
  headerAction,
  children,
  moreCount,
  onShowMore,
  isExpanded,
  viewMode,
  onViewModeChange,
}) => (
  <Section>
    <Header>
      <HeaderLeft>
        <Label>{label}</Label>
        {headerSlot}
      </HeaderLeft>
      {onViewModeChange && viewMode && <ViewModeSwitcher viewMode={viewMode} onChange={onViewModeChange} />}
      {headerAction}
    </Header>
    {children}
    {onShowMore && (isExpanded || (moreCount != null && moreCount > 0)) && (
      <ShowMoreRow>
        <NgSectionLink onClick={onShowMore}>
          {isExpanded ? 'Show less' : `View ${moreCount} more ${(moreLabel ?? label).toLowerCase()} →`}
        </NgSectionLink>
      </ShowMoreRow>
    )}
  </Section>
);
